import { Info, ArrowSquareOut } from "@phosphor-icons/react";

// Single source of truth for the statutory basis behind each calculated figure.
// `reviewedYear` = latest year the rates were confirmed; `jur` drives the
// current-tax-year comparison (UK = 6 Apr year, IE = calendar year).
export const RULE_REFS = {
  ie_mileage: {
    basis: "Civil Service Motor Travel rates (Dept. of Public Expenditure & Reform), applied on a cumulative annual-distance band by engine size. EVs use the 1201–1500cc band.",
    reviewed: "2025 rates", reviewedYear: 2025, jur: "IE",
    source: "https://www.revenue.ie/en/employing-people/employee-expenses/travel-and-subsistence/civil-service-rates.aspx",
  },
  ie_err: {
    basis: "Civil Service subsistence day/overnight rates; Small Benefit Exemption €1,500 (max 5 non-cash benefits, Finance Act 2022); Remote Working Relief €3.20/day (Finance Act 2021). Reportable to Revenue under ERR.",
    reviewed: "2025 rates · ERR live", reviewedYear: 2025, jur: "IE",
    source: "https://www.revenue.ie/en/employing-people/employee-expenses/enhanced-reporting-requirements/index.aspx",
  },
  uk_mileage: {
    basis: "HMRC Approved Mileage Allowance Payments (AMAP), s.230 ITEPA 2003. Car/van 55p first 10,000 business miles then 25p (rate rose from 45p on 6 Apr 2026); motorcycles 24p; bicycles 20p; passengers 5p/mile. The 10,000-mile band resets each tax year.",
    reviewed: "2026/27", reviewedYear: 2026, jur: "UK",
    source: "https://www.gov.uk/travel-mileage-fuel-rates-allowances",
  },
  uk_subsistence: {
    basis: "HMRC benchmark subsistence scale rates — payable without receipts where the qualifying conditions are met (£5 / £10, +£15 ongoing past 8pm). £25 per 24-hour period maximum.",
    reviewed: "2026/27", reviewedYear: 2026, jur: "UK",
    source: "https://www.gov.uk/guidance/expenses-and-benefits-cash-sum-payments-to-employees",
  },
  us_mileage: {
    basis: "IRS standard mileage rate (Rev. Proc. / annual notice), applied by trip date: 72.5¢/mi (1 Jan–30 Jun 2026) and 76¢/mi (1 Jul–31 Dec 2026) for business use. Alternatively deduct actual vehicle costs. The employer entity's jurisdiction (US/IRS) governs, regardless of where the trip takes place.",
    reviewed: "2026 IRS rates", reviewedYear: 2026, jur: "US",
    source: "https://www.irs.gov/tax-professionals/standard-mileage-rates",
  },
  deadlines: {
    basis: "IE — CRO Annual Return (B1) due ARD + 56 days; CT1 within ~8 months of year-end; VAT bi-monthly by the 19th (23rd via ROS). UK — Companies House CS01 (incorporation anniversary + 14 days) & accounts (first set 21 months, then 9 months after ARD); Corporation Tax pay 9 months + 1 day, CT600 12 months; MTD VAT 1 calendar month + 7 days after quarter end.",
    reviewed: "2026/27", reviewedYear: 2026, jur: "UK",
    source: "https://www.gov.uk/running-a-limited-company/company-and-accounting-records",
  },
};

// Current tax year for a jurisdiction: UK runs 6 Apr → 5 Apr, IE is calendar-year.
export function currentTaxYear(jur) {
  const now = new Date();
  const y = now.getFullYear();
  if (jur === "UK") {
    const afterApr6 = now.getMonth() > 3 || (now.getMonth() === 3 && now.getDate() >= 6);
    return afterApr6 ? y : y - 1;
  }
  return y;
}

export default function RuleNote({ id }) {
  const r = RULE_REFS[id];
  if (!r) return null;
  const cty = currentTaxYear(r.jur);
  const stale = r.reviewedYear < cty;
  const reverifyLabel = r.jur === "UK" ? `${cty}/${String(cty + 1).slice(2)}` : `${cty}`;

  return (
    <div className="glass rounded-xl px-4 py-3 flex items-start gap-2.5" data-testid={`rule-note-${id}`}>
      <Info size={16} weight="fill" className="text-[#5B8DEF] mt-0.5 shrink-0" />
      <div className="text-xs text-white/55 leading-relaxed">
        <span className="text-white/75 font-medium">Basis: </span>{r.basis}{" "}
        <a href={r.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[#5B8DEF] hover:underline whitespace-nowrap">
          Official source <ArrowSquareOut size={11} weight="bold" />
        </a>
        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
          {stale && <span className="w-1.5 h-1.5 rounded-full bg-[#FFB020] shrink-0" title={`Rates last confirmed for ${r.reviewed}. Re-verify for ${reverifyLabel}.`} data-testid={`rule-stale-${id}`} />}
          <span className="text-white/35">Rates &amp; rules reviewed: {r.reviewed}</span>
          {stale && <span className="text-[#FFB020] normal-case tracking-normal">· re-verify for {reverifyLabel}</span>}
        </div>
      </div>
    </div>
  );
}
